import { Component, Inject, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { ImpressaoPessoaService } from '../../../../../shared/services/apis/pessoa/impressao-pessoa.service';
import { PessoaListaRequest } from '../../../../../shared/services/requests/pessoa/pessoa-lista.request';
import { PessoaImpressaoAgrupadaRequest } from '../../../../../shared/services/requests/pessoa/pessoa-impressao-agrupado.request';
import { ImpressaoGrupoStruct } from '../../../../../shared/services/structs/pessoa/impressao-grupo.struct';
import { CpfCnpjPipe } from '../../../../../shared/pipes/cpf-cnpj.pipe';
import { FoneMaskPipe } from '../../../../../shared/pipes/fone-mask.pipe';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';

export interface ImpressaoListaAgrupadaData {
  filtros: PessoaListaRequest;
}

@Component({
  selector: 'app-impressao-lista-agrupada',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatTableModule,
    ReactiveFormsModule,
    LoadingBoxComponent,
    CpfCnpjPipe,
    FoneMaskPipe,
  ],
  templateUrl: './impressao-lista-agrupada.component.html',
  styleUrls: ['./impressao-lista-agrupada.component.css'],
})
export class ImpressaoListaAgrupadaComponent implements OnInit {
  public step: 'filtro' | 'relatorio' = 'filtro';
  public formAgrupado: FormGroup;
  public isLoading: boolean = false;
  public relatorioAgrupado: ImpressaoGrupoStruct[] = [];
  public displayedColumns: string[] = ['nome', 'documento', 'cidade', 'contato', 'email'];

  constructor(
    public dialogRef: MatDialogRef<ImpressaoListaAgrupadaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoListaAgrupadaData,
    private impressaoService: ImpressaoPessoaService,
    private alertService: AlertService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initFormAgrupado();
  }

  private initFormAgrupado(): void {
    this.formAgrupado = this.fb.group({
      tipoAgrupamento: ['1', Validators.required],
      imprimeEndereco: [false],
    });
  }

  public submitAgrupado(): void {
    if (this.formAgrupado.invalid) return;

    this.step = 'relatorio';
    this.isLoading = true;

    const tipoAgrupamentoStr = this.formAgrupado.get('tipoAgrupamento')?.value;
    const tipoAgrupamento = parseInt(tipoAgrupamentoStr, 10) || 1;

    const request = new PessoaImpressaoAgrupadaRequest();
    Object.assign(request, this.data.filtros);
    request.tipoAgrupamento = tipoAgrupamento;
    request.imprimeEndereco = this.formAgrupado.get('imprimeEndereco')?.value;

    this.impressaoService.getListaAgrupada(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (!res.isError) {
          this.relatorioAgrupado = res.listaPessoaImpressaoAgrupada || [];
        } else {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.step = 'filtro';
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao gerar relatório agrupado', AlertType.error);
        this.step = 'filtro';
      },
    });
  }

  public imprimir(): void {
    const conteudoParaImprimir = document.querySelector('.modal-print-container');

    if (conteudoParaImprimir) {
      const janelaImpressao = window.open('', '', 'height=600,width=800');

      if (janelaImpressao) {
        const styles = Array.from(document.styleSheets)
          .map(styleSheet => {
            try {
              return Array.from(styleSheet.cssRules)
                .map(rule => rule.cssText)
                .join('');
            } catch (e) {
              return '';
            }
          })
          .join('');

        janelaImpressao.document.write(`
          <html>
            <head>
              <title>Imprimir Cadastros Agrupados</title>
              <base href="/">
              <style>
                ${styles}
                html, body {
                    height: auto !important;
                    min-height: 0 !important;
                    overflow: visible !important;
                    margin: 0 !important;
                    font-family: Roboto, "Helvetica Neue", sans-serif;
                    color: #000;
                    background: #fff;
                }
                .no-print { display: none !important; }
                .modal-print-container {
                    display: block !important;
                    width: 100% !important;
                    height: auto !important;
                    min-height: 0 !important;
                    overflow: visible !important;
                    box-shadow: none !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .mat-mdc-table { background: white !important; table-layout: fixed !important; width: 100% !important; }
                .mat-mdc-header-cell { color: black !important; font-weight: bold !important; border-bottom: 2px solid black !important; }
                .mat-mdc-cell { color: black !important; border-bottom: 1px solid #ddd !important; }
                th, td {
                    overflow-wrap: anywhere !important;
                    word-break: break-word !important;
                    white-space: normal !important;
                    padding: 4px 6px !important;
                    font-size: 12px !important;
                }
                .table-responsive { overflow: visible !important; }
                @page { size: A4 portrait; margin: 8mm; }
                .group-header { font-weight: bold; font-size: 14px; margin-top: 20px; padding: 5px; background: #eee; border-left: 4px solid #333; }
                .title-header { text-align: center; margin-bottom: 20px; }
                h2 { font-size: 16px; margin: 0; }
              </style>
            </head>
            <body>
              ${conteudoParaImprimir.innerHTML}
            </body>
          </html>
        `);

        janelaImpressao.document.close();
        janelaImpressao.focus();

        setTimeout(() => {
          janelaImpressao.print();
          janelaImpressao.close();
        }, 800);
      }
    }
  }

  public voltar(): void {
    this.step = 'filtro';
    this.dialogRef.updateSize('auto', 'auto'); // Reset size? Or keep it large.
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
